import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { Loader, ChevronLeft, Phone, User, CheckCircle2, XCircle, Clock, AlertCircle, MessageCircle, Search, Trash2, Image as ImageIcon, ExternalLink, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import DeleteConfirmModal from './DeleteConfirmModal';

interface SellerSubmission {
  id: string;
  seller_name: string;
  seller_phone: string;
  image_url: string;
  status: 'pending' | 'accepted' | 'rejected';
  admin_notes: string | null;
  created_at: string;
}

interface ProcessedSubmission extends SellerSubmission {
  allImages: string[];
}

export default function AdminSellerSubmissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<ProcessedSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSubmissions, setFilteredSubmissions] = useState<ProcessedSubmission[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<ProcessedSubmission | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const deletedIdsRef = React.useRef(new Set<string>());

  const fetchSubmissions = async () => {
    try {
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('seller_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      // Filter out deleted submissions
      const filteredData = (submissionsData || []).filter(submission => 
        !deletedIdsRef.current.has(submission.id)
      );

      // Process submissions to extract all images
      const processedSubmissions = filteredData.map(submission => {
        const additionalImages = submission.admin_notes?.match(/https:\/\/[^\s,]+/g) || [];
        return {
          ...submission,
          allImages: [submission.image_url, ...additionalImages]
        };
      });

      setSubmissions(processedSubmissions);
      setFilteredSubmissions(processedSubmissions);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    }
  };

  useEffect(() => {
    const checkAdminAndFetchSubmissions = async () => {
      try {
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          throw new Error('Not authorized');
        }

        setIsAdmin(true);
        await fetchSubmissions();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchSubmissions();

    // Set up real-time subscription
    const channel = supabase.channel('seller-submissions');
    
    channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'seller_submissions'
        },
        async (payload) => {
          // Only fetch if it's not a delete event or if the deleted ID isn't in our tracking set
          if (payload.eventType !== 'DELETE' || !deletedIdsRef.current.has(payload.old?.id)) {
            await fetchSubmissions();
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredSubmissions(submissions);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = submissions.filter(submission => 
      submission.seller_name.toLowerCase().includes(query) ||
      submission.seller_phone.includes(query) ||
      submission.status.toLowerCase().includes(query)
    );

    setFilteredSubmissions(filtered);
  }, [searchQuery, submissions]);

  const updateStatus = async (id: string, status: 'accepted' | 'rejected') => {
    try {
      const { error: updateError } = await supabase
        .from('seller_submissions')
        .update({ status })
        .eq('id', id);

      if (updateError) throw updateError;

      // Update local state
      const updatedSubmissions = submissions.map(submission => 
        submission.id === id ? { ...submission, status } : submission
      );
      setSubmissions(updatedSubmissions);
      setFilteredSubmissions(updatedSubmissions.filter(submission => 
        submission.seller_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        submission.seller_phone.includes(searchQuery) ||
        submission.status.toLowerCase().includes(searchQuery.toLowerCase())
      ));
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedSubmission || isDeleting) return;
    
    try {
      setIsDeleting(true);

      // Delete the submission record first
      const { error: deleteError } = await supabase
        .from('seller_submissions')
        .delete()
        .eq('id', selectedSubmission.id);

      if (deleteError) throw deleteError;

      // Try to delete images from storage
      for (const imageUrl of selectedSubmission.allImages) {
        try {
          const urlParts = imageUrl.split('/');
          const fileName = urlParts[urlParts.length - 1];
          const folderName = urlParts[urlParts.length - 2];
          const imagePath = `${folderName}/${fileName}`;
          
          await supabase.storage
            .from('collectible-images')
            .remove([imagePath]);
        } catch (imageError) {
          console.error('Error deleting image:', imageError);
          // Continue with other images even if one fails
        }
      }

      // Track deleted ID
      deletedIdsRef.current.add(selectedSubmission.id);

      // Update local state
      const updatedSubmissions = submissions.filter(s => s.id !== selectedSubmission.id);
      setSubmissions(updatedSubmissions);
      setFilteredSubmissions(updatedSubmissions.filter(submission => 
        submission.seller_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        submission.seller_phone.includes(searchQuery) ||
        submission.status.toLowerCase().includes(searchQuery.toLowerCase())
      ));

      setShowDeleteModal(false);
      setSelectedSubmission(null);
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert('Failed to delete submission. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'accepted':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'accepted':
        return 'bg-green-50 text-green-700 border-green-100';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <Link to="/" className="text-primary-600 hover:text-primary-800 font-medium">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Seller Submissions - Admin</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Seller Submissions</h1>
          </div>
          <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
            {filteredSubmissions.length} Submissions
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, or status..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {filteredSubmissions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matching submissions found' : 'No Submissions Yet'}
            </h3>
            <p className="text-gray-600">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Seller submissions will appear here when users submit their coins/notes.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    <div className="w-full sm:w-auto">
                      <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
                        {submission.allImages.map((imageUrl, index) => (
                          <div key={index} className="relative aspect-square w-full sm:w-48">
                            <img
                              src={imageUrl}
                              alt={`Submission ${index + 1} by ${submission.seller_name}`}
                              className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setSelectedImage(imageUrl)}
                            />
                            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-full">
                              Image {index + 1}
                            </div>
                            <a
                              href={imageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/75 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex-grow">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                        <div>
                          <h2 className="text-lg font-medium text-gray-900">
                            {submission.seller_name}
                          </h2>
                          <div className="flex items-center gap-3 mt-1">
                            <time className="text-sm text-gray-500">
                              Submitted {format(new Date(submission.created_at), 'PPp')}
                            </time>
                          </div>
                        </div>
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeColor(submission.status)}`}>
                          {getStatusIcon(submission.status)}
                          {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 mb-6">
                        <a
                          href={`tel:${submission.seller_phone}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          <Phone className="h-4 w-4" />
                          {submission.seller_phone}
                        </a>
                        <a
                          href={`https://wa.me/${submission.seller_phone.replace(/\+/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          <MessageCircle className="h-4 w-4" />
                          WhatsApp
                        </a>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        {submission.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(submission.id, 'accepted')}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Accept
                            </button>
                            <button
                              onClick={() => updateStatus(submission.id, 'rejected')}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                              <XCircle className="h-4 w-4" />
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setShowDeleteModal(true);
                          }}
                          disabled={isDeleting}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSubmission(null);
        }}
        onConfirm={handleDelete}
        title={`submission from ${selectedSubmission?.seller_name || ''}`}
      />

      {/* Image Preview Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh]">
            <img
              src={selectedImage}
              alt="Full size preview"
              className="w-full h-full object-contain"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/75 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <a
              href={selectedImage}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-4 left-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/75 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-6 w-6" />
            </a>
          </div>
        </div>
      )}
    </>
  );
}